import {
  CompRef,
  CompStructure,
  DocumentServicesObject,
  SingleLayoutData,
} from '@wix/document-services-types';
import { WidgetPointer } from '@wix/editor-platform-host-integration-apis';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import { HEADER_SYSTEM_SLOT } from '../constants';
import { HeaderPluginsApi, PluginCsmEssentials } from '../types';
import { createWidgetPluginComponentDefinition } from '../utils';
import {
  getOrCreateInternalContainer,
  ZERO_SPX_BOX,
} from './internalContainer';
import { getAppDataByCompRef } from '../utils/getAppData';

const STACK_ITEM_LAYOUT: SingleLayoutData['itemLayout'] = {
  type: 'StackItemLayout',
  margins: ZERO_SPX_BOX,
  alignSelf: 'start',
  order: 1,
};

const createHeaderPluginComponentDefinition = async (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  widgetPointer: WidgetPointer,
): Promise<CompStructure> => {
  const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
    widgetPointer.appDefinitionId,
  );

  if (!appData?.applicationId) {
    throw new Error(
      `Application with appDefinitionId: ${widgetPointer.appDefinitionId} is missing`,
    );
  }

  return createWidgetPluginComponentDefinition(
    documentServicesAPI,
    widgetPointer,
    appData.applicationId,
    experimentsAPI,
    {
      definitionOverrides: (componentDefinition) => ({
        ...componentDefinition,
        layouts: {
          ...componentDefinition.layouts,
          containerLayout: {},
          itemLayout: STACK_ITEM_LAYOUT,
        },
      }),
    },
  );
};

const appendPluginToInternalContainer = async (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  internalContainerRef: CompRef,
  widgetPointer: WidgetPointer,
): Promise<CompRef> => {
  const widgetComponent = await createHeaderPluginComponentDefinition(
    documentServicesAPI,
    experimentsAPI,
    widgetPointer,
  );
  const existingChildren =
    documentServicesAPI.components.getChildren(internalContainerRef);
  const insertIndex = existingChildren.length;

  return documentServicesAPI.components.add(
    internalContainerRef,
    widgetComponent,
    undefined,
    insertIndex,
  );
};

export const createHeaderPluginsApi = (
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): HeaderPluginsApi => ({
  installHeaderPlugin: async (
    _context,
    slotPointer,
    widgetPointer,
    _actionContext,
  ): Promise<CompRef> => {
    const path = slotPointer.toBuilder();
    const internalContainerRef = getOrCreateInternalContainer(
      documentServicesAPI,
      path,
    );

    return appendPluginToInternalContainer(
      documentServicesAPI,
      experimentsAPI,
      internalContainerRef,
      widgetPointer,
    );
  },

  isHeaderPlugin: (compRef: CompRef): boolean => {
    let widgetId: string | undefined;
    let appDefinitionId: string | undefined;
    try {
      ({ widgetId, appDefinitionId } = getAppDataByCompRef(
        documentServicesAPI,
        compRef,
      ));
    } catch {
      return false;
    }
    if (!widgetId || !appDefinitionId) {
      return false;
    }

    const appData =
      documentServicesAPI.platform.getAppDataByAppDefId(appDefinitionId);

    const widgetPlugin = appData?.components?.find(
      (component) =>
        component.type === 'WIDGET_PLUGIN' &&
        (component.data as PluginCsmEssentials).referenceComponentId ===
          widgetId,
    );

    if (!widgetPlugin) {
      return false;
    }

    const placementsSlotIds =
      (widgetPlugin.data as PluginCsmEssentials).placementsSlotIds ?? [];

    return placementsSlotIds.includes(HEADER_SYSTEM_SLOT.id);
  },
});
